import { useEffect, useState } from 'react';
import { api } from '../../services/api';
import { StockMove } from '../../services/types';
import { CompactTable } from '../../components/ui/CompactTable';
import { TableFilterBar } from '../../components/table-filters/TableFilterBar';
import { includesNormalized } from '../../components/table-filters/filterPredicates';
import { useTableFilterState } from '../../hooks/useTableFilterState';
import { FlowEntryBanner } from '../../components/workflow/FlowEntryBanner';

export function StockLedger() {
    const [moves, setMoves] = useState<StockMove[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const { state, setFilter, clearAll, isDirty } = useTableFilterState({
        q: '',
        type: 'all'
    });

    useEffect(() => {
        api.inventory.getLedger().then(data => {
            setMoves(data);
            setIsLoading(false);
        });
    }, []);

    const formatDate = (dateString: string) => {
        try {
            const d = new Date(dateString);
            return d.toLocaleDateString('en-GB') + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch (e) {
            return dateString;
        }
    };

    const columns = [
        { 
            header: 'Date', 
            accessor: (m: StockMove) => formatDate(m.transaction_date),
            className: 'w-40 text-sm text-slate-600 text-center'
        },
        { header: 'Item', accessor: (m: StockMove) => <span className="font-mono text-sm">{m.item_code}</span>, className: 'w-32 text-center' },
        { header: 'Description', accessor: (m: StockMove) => <span className="truncate max-w-[36rem] text-left">{m.item_name}</span>, className: 'w-72 text-left' },
        { 
            header: 'Type', 
            accessor: (m: StockMove) => (
                <div className="flex justify-center">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold uppercase ${
                        m.transaction_type === 'GRN' ? 'bg-green-100 text-green-700' :
                        m.transaction_type === 'ISSUE' ? 'bg-red-100 text-red-700' :
                        'bg-blue-100 text-blue-700'
                    }`}>
                        {m.transaction_type}
                    </span>
                </div>
            ),
            className: 'w-24 text-center'
        },
        {
            header: 'Flow',
            accessor: (m: StockMove) => (
                <div className="flex justify-center">
                    <span className="text-xs text-slate-600">{m.flow_intent || '-'}</span>
                </div>
            ),
            className: 'w-32 text-center'
        },
        { 
            header: 'Qty', 
            accessor: (m: StockMove) => {
                const qty = Number(m.quantity || 0);
                const cls = qty < 0 ? 'text-destructive' : qty === 0 ? 'text-slate-500' : 'text-emerald-600';
                return (<div className="flex justify-center"><span className={`font-medium ${cls}`}>{qty > 0 ? `+${qty.toFixed(2)}` : qty.toFixed(2)}</span></div>);
            },
            className: 'w-24 text-center' 
        },
        { header: 'Rate', accessor: (m: StockMove) => <div className="flex justify-center">{Number(m.unit_price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>, className: 'w-28 text-center' },
        {
            header: 'Valuation',
            accessor: (m: StockMove) => <div className="flex justify-center text-xs text-slate-600">{m.valuation_source || '-'}</div>,
            className: 'w-28 text-center'
        },
        { header: 'Total Value', accessor: (m: StockMove) => <div className="flex justify-center font-semibold">{Number(m.calculated_cost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>, className: 'w-32 text-center' },
        { header: 'Source', accessor: (m: StockMove) => (
            <div className="flex justify-center">
                <span className="text-xs text-slate-600 truncate max-w-[140px] text-center">
                    {[m.source_type, m.source_id, m.source_line_id].filter((x) => x !== null && x !== undefined && `${x}` !== '').join(':') || '-'}
                </span>
            </div>
        ), className: 'w-40 text-center' },
        { header: 'Reference', accessor: (m: StockMove) => (
            <div className="flex justify-center">
                <span className="text-sm text-slate-600 italic truncate max-w-[160px] text-center">{m.reference_doc}</span>
            </div>
        ), className: 'w-44 text-center' }
    ];

    if (isLoading) return <div>Loading ledger...</div>;

    const filteredMoves = moves.filter((move) => {
        const matchesSearch = includesNormalized(
            [
                formatDate(move.transaction_date),
                move.item_code,
                move.item_name,
                move.transaction_type,
                move.flow_intent,
                move.source_type,
                move.source_id,
                move.source_line_id,
                move.valuation_source,
                move.reference_doc,
                move.quantity,
                move.unit_price,
                move.calculated_cost
            ].join(' '),
            state.q
        );
        const matchesType = state.type === 'all' || move.transaction_type === state.type;
        return matchesSearch && matchesType;
    });

    return (
        <div className="space-y-4">
            <FlowEntryBanner
                title="Ledger Flow Guidance"
                pathLabel="Inventory Audit Trail"
                nextHint="ADJUST = internal operational updates, GRN = valued supplier receipt, ISSUE = stock outflow."
                helpModule="inventory"
            />
            <div className="flex justify-between items-end">
                <div>
                    <h2 className="text-xl font-semibold">Stock Ledger</h2>
                    <p className="text-sm text-muted-foreground text-slate-500">Full audit trail of inventory movements - {filteredMoves.length} of {moves.length}</p>
                </div>
            </div>

            <TableFilterBar
                searchValue={state.q}
                onSearchChange={(value) => setFilter('q', value)}
                searchPlaceholder="Search by item, transaction type, reference..."
                controls={[
                    {
                        key: 'type',
                        label: 'Type',
                        type: 'select',
                        value: state.type,
                        onChange: (value) => setFilter('type', value),
                        options: [
                            { value: 'all', label: 'All Types' },
                            { value: 'GRN', label: 'GRN' },
                            { value: 'ISSUE', label: 'Issue' },
                            { value: 'ADJUST', label: 'Adjustment' }
                        ]
                    }
                ]}
                isDirty={isDirty}
                onClear={clearAll}
            />

            <div className="border rounded-md bg-card">
                <CompactTable 
                    data={filteredMoves} 
                    columns={columns} 
                    keyField="move_id" 
                />
            </div>
        </div>
    );
}
